import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Copy, Trash2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

function statusOf(b) {
  if (b.is_ended) return { label: "Ended", cls: "bg-black/5 text-[var(--muted)]" };
  if (b.is_live) return { label: "Live now", cls: "bg-red-600 text-white" };
  return { label: "Scheduled", cls: "bg-[var(--bg-warm)] text-[var(--ink)]" };
}

function BroadcastRow({ b, onChanged }) {
  const st = statusOf(b);
  const link = `${window.location.origin}/live/${b.broadcast_id}`;
  async function end() {
    try { await api.patch(`/broadcasts/${b.broadcast_id}`, { is_ended: true }); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`golive-row-${b.broadcast_id}`}>
      <div className="flex-1 min-w-0">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
        <p className="font-serif text-xl mt-2 truncate">{b.title}</p>
        <p className="text-xs text-[var(--muted)]">{new Date(b.scheduled_at).toLocaleString()}</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Guest link copied"); }} className="btn-outline-gold text-xs py-2" data-testid={`golive-copy-${b.broadcast_id}`}><Copy className="w-3.5 h-3.5" /> Copy guest link</button>
        {!b.is_ended && <Link to={`/live/${b.broadcast_id}`} className="btn-gold text-xs py-2" data-testid={`golive-start-${b.broadcast_id}`}><Radio className="w-3.5 h-3.5" /> {b.is_live ? "Rejoin" : "Go live"}</Link>}
        {b.is_live && <button onClick={end} className="text-xs px-3 py-2 rounded-full border border-red-200 text-red-600" data-testid={`golive-end-${b.broadcast_id}`}><Trash2 className="w-3.5 h-3.5 inline" /> End</button>}
      </div>
    </div>
  );
}

export default function GoLivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [wedding, setWedding] = useState(null);
  const [form, setForm] = useState({ title: "Our ceremony, live", scheduled_at: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/broadcasts/mine").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/weddings/mine").then((r) => setWedding(r.data)).catch(() => {});
  }, []);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/broadcasts", {
        title: form.title,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        wedding_id: wedding?.wedding_id || null,
      });
      toast.success("Broadcast scheduled");
      setForm({ ...form, scheduled_at: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="golive-page">
      <div>
        <p className="font-script text-[var(--gold)] text-3xl">Locked In Live</p>
        <h1 className="font-serif font-light text-4xl">Stream your big day</h1>
        <p className="text-sm text-[var(--muted)] mt-2">Schedule a broadcast, share the link, and guests anywhere can watch and send love in the live chat. No app needed.</p>
      </div>
      {!wedding && !isAdmin && (
        <p className="text-sm bg-[var(--bg-warm)] rounded-xl px-4 py-3" data-testid="golive-no-wedding">Create your wedding site first, then you can schedule a broadcast for your big day. <a href="/onboarding" className="text-[var(--gold)] underline">Set it up</a></p>
      )}
      <form onSubmit={create} className="bg-white border border-[var(--line)] rounded-2xl p-6 grid md:grid-cols-[1fr_220px_auto] gap-3 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Title</label>
          <input data-testid="golive-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-luxe" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Starts</label>
          <input data-testid="golive-when" required type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="input-luxe" />
        </div>
        <button data-testid="golive-create" disabled={busy || (!wedding && !isAdmin)} className="btn-gold"><CalendarPlus className="w-4 h-4" /> {busy ? "Saving…" : "Schedule"}</button>
      </form>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-[var(--muted)] text-center py-10" data-testid="golive-empty">No broadcasts yet. Schedule your first one above.</p>}
        {rows.map((b) => <BroadcastRow key={b.broadcast_id} b={b} onChanged={load} />)}
      </div>
    </div>
  );
}
